import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, FileText, Users, Building2, TrendingUp, Landmark, Zap, Compass, PenLine, Radio, ArrowRight } from 'lucide-react';
import WaitlistModal from '../components/WaitlistModal';
import { ShapeLandingHero } from '../components/ShapeLandingHero';
import { MagnetizeButton } from '../components/MagnetizeButton';
import AnimatedGradient from '../components/AnimatedGradient';
import GlobeSection from '../components/GlobeSection';

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; color: #0B1220; overflow-x: hidden; }
  .reveal { opacity: 0; transform: translateY(18px); transition: opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1); }
  .reveal.delay-1 { transition-delay: 0.08s; }
  .reveal.delay-2 { transition-delay: 0.16s; }
  .reveal.delay-3 { transition-delay: 0.24s; }
  .reveal.delay-4 { transition-delay: 0.32s; }
  .reveal.revealed { opacity: 1; transform: translateY(0); }
  .gradient-text { background: linear-gradient(135deg,#4E6C7E 0%,#3A5468 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .card-hover { transition: transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s cubic-bezier(0.22,1,0.36,1); cursor: default; }
  .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 50px rgba(0,0,0,0.35); }
  .ticker-inner { display: inline-flex; animation: ticker 32s linear infinite; white-space: nowrap; }
  @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

  .hamburger-btn span {
    display: block; width: 22px; height: 2px; background: currentColor;
    border-radius: 2px; transition: box-shadow 0.2s, background 0.35s;
  }
  .hamburger-btn:hover span {
    box-shadow: 0 0 8px rgba(78,108,126,0.9), 0 0 18px rgba(78,108,126,0.5);
    background: #4E6C7E !important;
  }

  .menu-overlay { position: fixed; inset: 0; background: rgba(11,18,32,0.7); z-index: 200; opacity: 0; pointer-events: none; transition: opacity 0.35s; backdrop-filter: blur(6px); }
  .menu-overlay.open { opacity: 1; pointer-events: all; }

  .holo-card {
    position: relative; border-radius: 24px; padding: 40px 36px; cursor: default;
    transform-style: preserve-3d; transition: transform 0.1s ease;
    background: linear-gradient(135deg,rgba(255,248,244,0.95) 0%,rgba(255,235,220,0.9) 50%,rgba(255,248,244,0.95) 100%);
    border: 1px solid rgba(78,108,126,0.2); overflow: hidden; will-change: transform;
  }
  .holo-card::before {
    content:''; position:absolute; inset:0; border-radius:24px;
    background: radial-gradient(circle at var(--mx,50%) var(--my,50%), rgba(108,150,156,0.22) 0%, rgba(78,108,126,0.08) 40%, transparent 70%);
    opacity: var(--holo-opacity,0); transition: opacity 0.3s; pointer-events:none; z-index:1;
  }
  .holo-card::after {
    content:''; position:absolute; inset:0; border-radius:24px;
    background: linear-gradient(105deg,transparent 20%,rgba(124,172,183,0.08) 30%,rgba(78,108,126,0.12) 45%,rgba(108,150,156,0.08) 55%,transparent 70%);
    background-size:200% 200%; background-position: var(--bg-x,50%) var(--bg-y,50%);
    opacity: var(--holo-opacity,0); transition: opacity 0.3s; pointer-events:none; z-index:1;
  }
  .holo-card .holo-shine { position:absolute; inset:0; border-radius:24px; background: radial-gradient(ellipse at var(--mx,50%) var(--my,50%),rgba(255,255,255,0.35) 0%,transparent 55%); opacity:var(--holo-opacity,0); transition:opacity 0.3s; pointer-events:none; z-index:2; }
  .holo-card .holo-content { position:relative; z-index:3; }

  :root { --hero-inset: 16px; --hero-radius: 28px; }
  @media (max-width: 640px) { :root { --hero-inset: 10px; --hero-radius: 20px; } }
  section { padding: 88px 24px; }
  @media(max-width:640px){ section { padding: 60px 18px; } }
  @media(max-width:500px){ .perk-grid { grid-template-columns: 1fr !important; } }
`;

const useScrollReveal = () => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};


/* ─── Vertical animated menu ─────────────────────────────── */
const NAV_ITEMS = [
  { label: 'Problem',      id: 'problem' },
  { label: 'How it works', id: 'how-it-works' },
  { label: 'Cities',       id: 'cities' },
];

function VerticalMenu({ onClose }) {
  const scrollTo = id => {
    onClose();
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 400);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 40px' }}>
      {NAV_ITEMS.map((item, i) => (
        <motion.div
          key={item.id}
          initial="initial"
          whileHover="hover"
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => scrollTo(item.id)}
        >
          <motion.div
            variants={{
              initial: { x: '-100%', opacity: 0, color: '#4E6C7E' },
              hover:   { x: 0,       opacity: 1, color: '#4E6C7E' },
            }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <ArrowRight strokeWidth={3} style={{ width: 32, height: 32 }} />
          </motion.div>
          <motion.span
            variants={{
              initial: { x: -32, color: '#0B1220' },
              hover:   { x: 0,   color: '#4E6C7E', skewX: -4 },
            }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize: 'clamp(28px,5vw,42px)', fontWeight: 700, lineHeight: 1.15, display: 'block' }}
          >
            {item.label}
          </motion.span>
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Navbar ─────────────────────────────────────────────── */
function Navbar({ onWaitlist }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const barColor = scrolled ? '#0B1220' : '#fff';

  return (
    <>
      <style>{globalStyles}</style>

      {/* Overlay */}
      <div className={`menu-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />

      {/* Slide-in drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'fixed', top: 0, left: 0, width: 'min(420px,90vw)', height: '100vh',
              background: '#fff', zIndex: 300, display: 'flex', flexDirection: 'column',
              justifyContent: 'center', paddingBottom: 60,
              boxShadow: '8px 0 40px rgba(0,0,0,0.12)',
            }}
          >
            {/* Close */}
            <button
              onClick={() => setMenuOpen(false)}
              style={{ position: 'absolute', top: 22, right: 22, background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: 22, lineHeight: 1, padding: 6 }}
            >✕</button>

            {/* Logo inside menu */}
            <div style={{ position: 'absolute', top: 18, left: 28 }}>
              <img src="/tandm-logo-primary.png" alt="Tandm" style={{ height: 48, objectFit: 'contain' }} />
            </div>

            <VerticalMenu onClose={() => setMenuOpen(false)} />

            {/* Bottom CTA */}
            <div style={{ position: 'absolute', bottom: 36, left: 40, right: 40 }}>
              <MagnetizeButton onClick={() => { setMenuOpen(false); onWaitlist(); }} style={{ width: '100%' }}>
                Get started
              </MagnetizeButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top nav bar */}
      <nav style={{
        position: 'fixed', top: scrolled ? 0 : 'calc(var(--hero-inset, 16px) + 22px)', left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.94)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(237,232,227,0.7)' : '1px solid transparent',
        transition: 'all 0.35s ease', padding: '0 28px',
      }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', height: 62, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              className="hamburger-btn"
              onClick={() => setMenuOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 5, padding: 4, color: barColor }}
            >
              <span /><span /><span />
            </button>
            <img
              src={scrolled ? "/tandm-logo-primary.png" : "/tandm-logo-on-dark.png"}
              alt="Tandm"
              style={{ height: scrolled ? 40 : 44, objectFit: 'contain' }}
            />
          </div>
          <MagnetizeButton onClick={onWaitlist} style={{ fontSize: 14, padding: '10px 22px', minWidth: 'unset' }}>
            Get started
          </MagnetizeButton>
        </div>
      </nav>
    </>
  );
}

/* ─── Problem ────────────────────────────────────────────── */
const companies = ['Goldman Sachs','Macquarie','KKR','BlackRock','Barclays','Citi','Jefferies','Blackstone','Fidelity','Revolut','Stripe','Carlyle','JP Morgan','Morgan Stanley','UBS'];
const problems = [
  { Icon: Search,   title: 'Scattered listings',   desc: 'Hours wasted searching across LinkedIn, company sites, and forums just to find relevant roles.' },
  { Icon: FileText, title: 'Generic applications', desc: 'Re-writing cover letters and competency answers from scratch for every single role.' },
  { Icon: Users,    title: 'No insider access',    desc: 'Not knowing who to reach out to or how after you have applied.' },
];

function CompanyMarquee() {
  const items = [...companies, ...companies];
  return (
    <div style={{ background: '#fff', padding: '40px 0 56px' }}>
      <div style={{ overflow: 'hidden' }}>
        <div className="ticker-inner">
          {items.map((c, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 14, padding: '0 28px' }}>
              <span style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize: 13, fontWeight: 600, color: '#64748B', whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>{c}</span>
              <span style={{ width: 3, height: 3, background: '#4E6C7E', borderRadius: '50%', opacity: 0.45 }} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProblemSection() {
  return (
    <div id="problem" style={{ position: 'relative', overflow: 'hidden', background: '#0B1220' }}>
      <AnimatedGradient
        customParams={{ color1:'#4E6C7E', color2:'#3A5468', color3:'#0B1220', rotation:100, proportion:72, scale:0.38, speed:18, distortion:6, swirl:22, swirlIterations:12, softness:85, offset:300, shape:'Edge', shapeSize:25 }}
        style={{ opacity: 0.75 }}
      />
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,rgba(11,18,32,0.55) 0%,rgba(11,18,32,0.2) 40%,rgba(11,18,32,0.6) 100%)', zIndex:1, pointerEvents:'none' }} />
      <div style={{ position:'relative', zIndex:2, maxWidth:1060, margin:'0 auto', padding:'80px 24px 96px' }}>
        <div style={{ textAlign:'center', marginBottom:60 }}>
          <motion.h2 initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.7 }}
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize:'clamp(28px,4vw,48px)', fontWeight:500, letterSpacing:'-0.03em', lineHeight:1.15, color:'#FFFFFF', marginBottom:16 }}>
            The application process<br />
            <span style={{ background:'linear-gradient(90deg,#7CACB7,#6C969C)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>is broken.</span>
          </motion.h2>
          <motion.p initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:0.15 }}
            style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'rgba(148,163,184,0.55)', fontSize:15, maxWidth:420, margin:'0 auto', lineHeight:1.7 }}>
            Students deserve a smarter way to land the roles they actually want.
          </motion.p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:20 }}>
          {problems.map(({ Icon, title, desc }, i) => (
            <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:0.1*i }}
              className="card-hover"
              style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(78,108,126,0.22)', borderRadius:20, padding:'30px 28px', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)' }}>
              <div style={{ width:48, height:48, background:'rgba(78,108,126,0.15)', border:'1px solid rgba(78,108,126,0.25)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
                <Icon size={20} color="#7CACB7" strokeWidth={1.8} />
              </div>
              <h3 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:17, fontWeight:700, color:'#fff', marginBottom:10 }}>{title}</h3>
              <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'rgba(148,163,184,0.55)', fontSize:14, lineHeight:1.7 }}>{desc}</p>
              <div style={{ marginTop:24, height:2, background:'linear-gradient(90deg,rgba(78,108,126,0.5),transparent)', borderRadius:2 }} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── How It Works ───────────────────────────────────────── */
const steps = [
  { num:'01', Icon:Compass, title:'Discover',      desc:'Browse curated Investment Banking, Private Equity, Consulting, Asset Management and Fintech internships across the UK and EU.' },
  { num:'02', Icon:PenLine, title:'Apply prepared', desc:'Keep one profile. We prepare your packet and fill employer portals in your browser — you always review and submit.' },
  { num:'03', Icon:Radio,   title:'Stay honest',    desc:'When a portal needs a robot check or email verify, we say so. No silent failures. No fake “100% automated.”' },
];
function HowItWorks() {
  return (
    <section id="how-it-works" style={{ background:'#F4F6F7' }}>
      <div style={{ maxWidth:1060, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:52 }}>
          <h2 className="reveal" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize:'clamp(28px,3.5vw,40px)', fontWeight:500, letterSpacing:'-0.02em', lineHeight:1.2 }}>
            Three steps to your<br />next internship.
          </h2>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:20 }}>
          {steps.map(({ num, Icon, title, desc }, i) => (
            <div key={i} className={`card-hover reveal delay-${i+1}`} style={{ background:'#FFFFFF', border:'1px solid #D9E2E6', borderRadius:14, padding:30 }}>
              <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:11, fontWeight:700, color:'#4E6C7E', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:16 }}>{num}</p>
              <div style={{ width:46, height:46, background:'#E8F1F3', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                <Icon size={20} color="#4E6C7E" strokeWidth={1.8} />
              </div>
              <h3 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:17, fontWeight:600, marginBottom:9 }}>{title}</h3>
              <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'#475569', fontSize:14, lineHeight:1.7 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Sectors ────────────────────────────────────────────── */
const sectors = [
  { Icon:Building2,  title:'Investment Banking', firms:'Barclays, Morgan Stanley, Jefferies, Citi & more' },
  { Icon:TrendingUp, title:'Asset Management',   firms:'Vanguard, BlackRock, Fidelity & leading funds' },
  { Icon:Landmark,   title:'Private Equity',     firms:'KKR, Blackstone, Carlyle & boutique firms' },
  { Icon:Zap,        title:'Fintech',             firms:'Revolut, Monzo, Stripe & high-growth ventures' },
];
function Sectors() {
  return (
    <div id="sectors" style={{ position:'relative', overflow:'hidden', background:'#0B1220' }}>
      {/* Lighter, slower animated gradient vs Problem */}
      <AnimatedGradient
        customParams={{ color1:'#6C969C', color2:'#3A5468', color3:'#0B1220', rotation:60, proportion:55, scale:0.28, speed:10, distortion:3, swirl:14, swirlIterations:8, softness:90, offset:100, shape:'Edge', shapeSize:30 }}
        style={{ opacity: 0.55 }}
      />
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom,rgba(11,18,32,0.5) 0%,rgba(11,18,32,0.15) 50%,rgba(11,18,32,0.5) 100%)', zIndex:1, pointerEvents:'none' }} />
      <section style={{ position:'relative', zIndex:2, padding:'88px 24px' }}>
        <div style={{ maxWidth:1060, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:52 }}>
            <h2 className="reveal" style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize:'clamp(28px,3.5vw,40px)', fontWeight:500, letterSpacing:'-0.02em', color:'#fff', marginBottom:12, lineHeight:1.2 }}>
              Roles across every<br />top sector.
            </h2>
            <p className="reveal delay-1" style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'rgba(148,163,184,0.5)', fontSize:15 }}>
              Tandm surfaces opportunities at the firms students actually want.
            </p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:16 }}>
            {sectors.map(({ Icon, title, firms }, i) => (
              <motion.div key={i} initial={{ opacity:0, y:24 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ duration:0.55, delay:0.08*i }}
                className="card-hover"
                style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(78,108,126,0.18)', borderRadius:18, padding:26, backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)' }}>
                <div style={{ width:44, height:44, background:'rgba(78,108,126,0.15)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                  <Icon size={20} color="#4E6C7E" strokeWidth={1.8} />
                </div>
                <h3 style={{ fontFamily: "Plus Jakarta Sans, sans-serif", fontSize:15, fontWeight:600, color:'#fff', marginBottom:6 }}>{title}</h3>
                <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'rgba(148,163,184,0.45)', fontSize:13, lineHeight:1.55 }}>{firms}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── Footer ─────────────────────────────────────────────── */
function Footer() {
  return (
    <footer style={{ background:'#0B1220', padding:'40px 28px', textAlign:'center' }}>
      <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}>
        <img src="/tandm-logo-on-dark.png" alt="Tandm" style={{ height:40, objectFit:'contain' }} />
      </div>
      <p style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color:'#64748B', fontSize:13 }}>© 2026 Tandm. Built for finance students.</p>
    </footer>
  );
}

/* ─── Main ───────────────────────────────────────────────── */
export default function LandingPage() {
  useScrollReveal();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const open = () => setWaitlistOpen(true);
  return (
    <div>
      <Navbar onWaitlist={open} />
      <ShapeLandingHero onCTA={open} />
      <CompanyMarquee />
      <ProblemSection />
      <HowItWorks />
      <Sectors />
      <GlobeSection />
      <Footer />
      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </div>
  );
}
